import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { formatBytes } from "@air/utilities";
import { subscriptionPlanMap } from "~/utils/SubscriptionUtils";
import { enterpriseOptions } from "../EnterprisePlanModal";
const getPlanTextMap = (planPricing, fixedPricingFlag)=>{
    const { pricingModel, limits } = planPricing;
    const planMap = {
        basic: {
            tagline: "Great for individuals or small projects.",
            priceDetail: "Enjoy Air for free",
            options: [
                "".concat(limits.storage ? formatBytes(limits.storage) : "Unlimited", " storage"),
                "".concat(limits.members ? limits.members : "1", " user"),
                "7 day deletion recovery",
                "Comments and annotations",
                "Content collection forms",
                "Import from Google Drive, Dropbox, and Box"
            ]
        },
        creator: {
            tagline: "Great for small teams or freelance projects.",
            priceDetail: pricingModel === "fixed" ? "per month" : "per member per month",
            options: [
                "".concat(limits.storage ? formatBytes(limits.storage) : "Unlimited", " storage"),
                "".concat(limits.members ? limits.members : "2", " users"),
                "30 day deletion recovery",
                "External sharing: commenting enabled",
                "Better suited for small team collaboration"
            ]
        },
        plus: {
            tagline: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    "This plan is ",
                    /*#__PURE__*/ _jsx("strong", {
                        children: "no longer available"
                    }),
                    ". Please contact help@air.inc to explore your options."
                ]
            }),
            priceDetail: pricingModel === "fixed" ? "per month" : "per member per month",
            options: [
                ...fixedPricingFlag ? [
                    "300 GB per workspace",
                    "5 members"
                ] : [
                    "200 GB per workspace",
                    "Unlimited members (pay per seat)"
                ],
                "30 day deletion recovery window",
                "GDrive, Dropbox & Box import",
                "Commenting and notifications",
                "Support for all file types",
                "PDF previews"
            ]
        },
        business: {
            tagline: "Best for teams ready to level up with powerful AI tools.",
            priceDetail: pricingModel === "fixed" ? "per month" : "per member per month",
            options: [
                "".concat(limits.storage ? formatBytes(limits.storage) : "Unlimited", " storage"),
                "AI-powered visual & document recognition",
                "Smart tags & summaries",
                "Video chapters & transcription",
                "Facial recognition",
                "Custom object recognition (coming soon)"
            ]
        },
        pro: {
            tagline: "Best for growing teams that need flexibility and scale.",
            priceDetail: pricingModel === "fixed" ? "per month" : "per member per month",
            options: [
                "".concat(limits.storage ? formatBytes(limits.storage) : "Unlimited", " storage"),
                "".concat(limits.members ? limits.members : "Unlimited", " users"),
                "90 day deletion recovery",
                "Optical Character Recognition (OCR)",
                "Comment-only user roles",
                "Advanced share link settings",
                "Slack & CDN integrations"
            ]
        },
        enterprise: {
            tagline: "For organizations with mature creative workflows.",
            customPriceDescription: "Let's chat!",
            options: enterpriseOptions
        }
    };
    return planMap;
};
const getStripePlanIds = (billingCycles)=>{
    var _billingCycles_find, _billingCycles_find1, _billingCycles_find2, _billingCycles_find3;
    return {
        annual: billingCycles === null || billingCycles === void 0 ? void 0 : (_billingCycles_find = billingCycles.find((param)=>{
            let { id } = param;
            return id === "annual";
        })) === null || _billingCycles_find === void 0 ? void 0 : _billingCycles_find.stripeId,
        monthly: billingCycles === null || billingCycles === void 0 ? void 0 : (_billingCycles_find1 = billingCycles.find((param)=>{
            let { id } = param;
            return id === "monthly";
        })) === null || _billingCycles_find1 === void 0 ? void 0 : _billingCycles_find1.stripeId,
        quarter: billingCycles === null || billingCycles === void 0 ? void 0 : (_billingCycles_find2 = billingCycles.find((param)=>{
            let { id } = param;
            return id === "quarter";
        })) === null || _billingCycles_find2 === void 0 ? void 0 : _billingCycles_find2.stripeId,
        semiAnnual: billingCycles === null || billingCycles === void 0 ? void 0 : (_billingCycles_find3 = billingCycles.find((param)=>{
            let { id } = param;
            return id === "semiAnnual";
        })) === null || _billingCycles_find3 === void 0 ? void 0 : _billingCycles_find3.stripeId
    };
};
export const planPricingToPlanInfo = (plan, previousPlanPricing, currentSubscriptionPlan, fixedPricingFlag)=>{
    var _planTextMap_id, _planTextMap_id1, _planTextMap_id2, _planTextMap_id3;
    const { id, billingCycles } = plan;
    const isCurrentPlanFromSelfServePlans = "isCurrentPlan" in plan ? plan.isCurrentPlan : false;
    const prices = {
        annual: 0,
        monthly: 0,
        quarter: 0,
        semiAnnual: 0
    };
    const annualPrice = billingCycles === null || billingCycles === void 0 ? void 0 : billingCycles.find((param)=>{
        let { id } = param;
        return id === "annual";
    });
    const monthly = billingCycles === null || billingCycles === void 0 ? void 0 : billingCycles.find((param)=>{
        let { id } = param;
        return id === "monthly";
    });
    if (annualPrice) {
        prices.annual = Math.round(annualPrice.planPrice / 12);
    }
    if (monthly) {
        prices.monthly = monthly.planPrice;
    }
    const stripePlanIds = getStripePlanIds(billingCycles);
    const planTextMap = getPlanTextMap(plan, fixedPricingFlag);
    const tagline = (_planTextMap_id = planTextMap[id]) === null || _planTextMap_id === void 0 ? void 0 : _planTextMap_id.tagline;
    const priceDetail = (_planTextMap_id1 = planTextMap[id]) === null || _planTextMap_id1 === void 0 ? void 0 : _planTextMap_id1.priceDetail;
    const customPriceDescription = (_planTextMap_id2 = planTextMap[id]) === null || _planTextMap_id2 === void 0 ? void 0 : _planTextMap_id2.customPriceDescription;
    var _planTextMap_id_options;
    const options = (_planTextMap_id_options = (_planTextMap_id3 = planTextMap[id]) === null || _planTextMap_id3 === void 0 ? void 0 : _planTextMap_id3.options) !== null && _planTextMap_id_options !== void 0 ? _planTextMap_id_options : [];
    const isPro = id === "pro";
    const isBusiness = id === "business";
    const isEnterprise = id === "enterprise";
    const isCreator = id === "creator";
    const isCurrentPlan = currentSubscriptionPlan === "enterprise" && isEnterprise ? true : isCurrentPlanFromSelfServePlans;
    const showRightBorder = id !== "enterprise";
    const optionsListHeader = (isPro || isBusiness || isEnterprise || isCreator) && previousPlanPricing ? "Everything in ".concat(subscriptionPlanMap[previousPlanPricing.id], ", and:") : "".concat(subscriptionPlanMap[id], " workspaces include:");
    return {
        id,
        stripePlanIds,
        tagline,
        prices,
        priceDetail,
        customPriceDescription,
        isCurrentPlan,
        showRightBorder,
        optionsListHeader,
        options
    };
};
